import { Descriptions, DescriptionsProps, Modal, Result } from "antd"
import { useEffect, useState } from "react"
import { SystemDescriptions } from "src/common/descriptions"
import { PayableContractDebit } from "src/common/models/payableContractDebit"
import { TransactionContractPayment } from "src/common/models/transactionContractPayment"
import { formatToCurrency, formatToDisplayDatetime } from "src/common/util"
import { PaymentForm } from "src/features/ServiceContract/CreateServiceContract/TicketSetction/CreatePayment/PaymentForm"
import { QuestionOutlined } from '@ant-design/icons';
import { FormActionButtons } from "src/components/FormActionButtons"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { createPayablePaymentApi } from "src/features/AccountPayable/state/action"
import { PayableContractPayment } from "src/common/models/payableContractDebitPayment"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { CREATE_PAYABLE_DEBIT_PAYMENT_API } from "src/features/AccountPayable/state/actionTypes"

interface Props {
    open: boolean
    onCancel: VoidFunction
    onAccept: VoidFunction
    payableDebit?: PayableContractDebit
}

export type ReduxProps = {
    createContractPaymentSuccess?: boolean
    isCreatingPayment: boolean
}

export const CreatePayableContractPayment = (props: Props) => {
    const dispatch = useDispatch()

    const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
    const [paymentData, setPaymentData] = useState<TransactionContractPayment>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        createContractPaymentSuccess: state.accountPayables.createPayablePaymentSuccess,
        isCreatingPayment: serviceLoading(state, [CREATE_PAYABLE_DEBIT_PAYMENT_API]),
    }))

    useEffect(() => {
        if (reduxProps.createContractPaymentSuccess && actionButtonClicked) {
            props.onAccept()
        }
    }, [reduxProps.createContractPaymentSuccess])

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT

    const items: DescriptionsProps['items'] = [
        {
            key: 'DESCRIPTION',
            label: descriptions.DESCRIPTIONS.DESCRIPTION,
            children: props.payableDebit?.description,
        },
        {
            key: 'CREATED_AT',
            label: descriptions.DESCRIPTIONS.CREATED_AT,
            children: formatToDisplayDatetime(props.payableDebit?.createdAt),
        },
        {
            key: 'AMOUN',
            label: descriptions.DESCRIPTIONS.AMOUNT,
            children: formatToCurrency(props.payableDebit?.amount.toString() || '0'),
        },
        {
            key: 'AMOUNT_PAYED',
            label: descriptions.DESCRIPTIONS.AMOUNT_PAYED,
            children: formatToCurrency(props.payableDebit?.amountPayed.toString() || '0'),
        },
        {
            key: 'OUTSTANDING',
            label: descriptions.DESCRIPTIONS.OUTSTANDING,
            children: formatToCurrency((Number(props.payableDebit?.amount) - Number(props.payableDebit?.amountPayed)).toString()),
        },
    ];

    const renderDebitData = () => {
        return <Descriptions items={items} />
    }

    const paymentSummary = () => {
        const summaryItems: DescriptionsProps['items'] = [
            {
                key: 'DESCRIPTION',
                label: descriptions.DESCRIPTIONS.DESCRIPTION,
                children: props.payableDebit?.description,
            },
            {
                key: 'AMOUN',
                label: descriptions.DESCRIPTIONS.AMOUNT,
                children: formatToCurrency(props.payableDebit?.amount.toString() || '0'),
            },
            {
                key: 'AMOUNT_PAYED',
                label: descriptions.DESCRIPTIONS.AMOUNT_PAYED,
                children: formatToCurrency(props.payableDebit?.amountPayed.toString() || '0'),
            },
            {
                key: 'OUTSTANDING',
                label: descriptions.DESCRIPTIONS.OUTSTANDING,
                children: formatToCurrency((Number(props.payableDebit?.amount) - Number(props.payableDebit?.amountPayed)).toString()),
                span: 2,
            },
            {
                key: 'CASH',
                label: descriptions.FORM.CASH.LABEL,
                children: formatToCurrency(paymentData?.cash?.toString() || '0'),
            },
            {
                key: 'TRANSACTION',
                label: descriptions.FORM.TRANSACTION.LABEL,
                children: formatToCurrency(paymentData?.transaction?.toString() || '0'),
            },
            {
                key: 'DISSCOUNT',
                label: descriptions.FORM.DISSCOUNT.LABEL,
                children: formatToCurrency(paymentData?.disscount?.toString() || '0'),
            },
            {
                key: 'TOTAL',
                label: descriptions.FORM.PAYMENT_TOTAL.LABEL,
                children: formatToCurrency(paymentData?.amount.toString() || '0'),
            },
            {
                key: 'COMMENT',
                label: descriptions.FORM.COMMENT.LABEL,
                children: paymentData?.comment || 'NA',
                span: 2,
            },

        ];

        return <div style={{ marginBottom: 12, }}>
            <Descriptions items={summaryItems} />
        </div>
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
            actionText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
            onCancel={function (): void {
                setShowConfirmation(false)
            }}
            onAccept={() => {
                if (!paymentData) {
                    return
                }

                const payload: PayableContractPayment = {
                    ...paymentData,
                    payableContractDebitId: paymentData?.contractId!,
                    datetime: '',
                    userId: 0,
                }
                setActionButtonClicked(true)
                dispatch(createPayablePaymentApi(payload))
            }}
            actionLoading={reduxProps.isCreatingPayment}
        />
    )

    const renderConfirmation = () => {
        return <Result
            icon={<QuestionOutlined />}
            title={descriptions.CONFIRM_MODAL.TITLE}
            subTitle={descriptions.CONFIRM_MODAL.DESCRIPTION}
            extra={[
                paymentSummary(),
                renderButtons(),
            ]}
        />
    }

    const onFinish = (payment: TransactionContractPayment) => {
        if (props.payableDebit) {
            setPaymentData(payment)
            setShowConfirmation(true)
        } else {
            // TODO: HANDLE PAYMENT EMBED IN CONTRACT CREATION
        }
    }

    return (
        <Modal
            title={descriptions.SECONDARY_TITLE}
            centered
            open={props.open}
            width={1000}
            onOk={props.onAccept}
            okText={descriptions.SAVE_BUTTON}
            cancelText={descriptions.CANCEL_BUTTON}
            footer={<></>}
            closable={false}
            zIndex={1200}
            getContainer={false}
            destroyOnClose={true}
        >
            {
                showConfirmation
                    ? renderConfirmation()
                    : <>
                        {renderDebitData()}
                        <hr />
                        <PaymentForm
                            onFinish={onFinish}
                            onCancel={props.onCancel}
                            contractDebit={props.payableDebit as any}
                            type="ACCOUNT_PAYABLES"
                        />
                    </>
            }
        </Modal>
    )
}
